import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Mail, RefreshCw, Copy, Check } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { get, post, del } from '@/shared/api/api_client';
import { Navbar } from '@/widgets/Navbar';
import { Card, Button, Modal } from '@/shared/ui';
import AddFamilyMember from '@/features/family/AddFamilyMember';
import ManageMembers from '@/features/family/ManageMembers';
import type { FamilyMember } from '@/entities/user';
import type { Family, Invitation } from '@/entities/family';
import { formatDate, formatRelativeTime } from '@/shared/utils/format';

// ---------------------------------------------------------------------------
// Invite link modal
// ---------------------------------------------------------------------------
interface InviteModalProps {
  familyId: string;
  onClose: () => void;
}

function InviteModal({ familyId, onClose }: InviteModalProps): React.ReactElement {
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const generateMutation = useMutation({
    mutationFn: () => post<{ shareableLink: string; id: string; expiresAt: string }>(
      `/families/${familyId}/invitations`,
      {},
    ),
    onSuccess: (data) => {
      setInviteUrl(data.shareableLink);
      void queryClient.invalidateQueries({ queryKey: ['invitations', familyId] });
    },
  });

  const { data: invitations = [], isLoading: loadingInvites } = useQuery({
    queryKey: ['invitations', familyId],
    queryFn: () => get<{ invitations: Invitation[] }>(`/families/${familyId}/invitations`).then(r => r.invitations),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => del<void>(`/families/${familyId}/invitations/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['invitations', familyId] }),
  });

  const handleCopy = async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen onClose={onClose} title="Invite a Parent" size="md">
      <div className="space-y-5">
        {/* Generate link */}
        <div>
          <p className="text-sm text-slate-600 mb-3">
            Generate a unique invite link to share with a co-parent or guardian. Links expire after 7 days.
          </p>
          <Button
            variant="primary"
            onClick={() => generateMutation.mutate()}
            isLoading={generateMutation.isPending}
            className="w-full"
          >
            <RefreshCw size={15} className="mr-1.5" />
            Generate Invite Link
          </Button>
        </div>

        {/* Generated link */}
        {inviteUrl && (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
            <p className="text-xs font-medium text-secondary-700 mb-1.5">Share this link:</p>
            <textarea
              readOnly
              value={inviteUrl}
              rows={3}
              aria-label="Invite link"
              className="w-full resize-none rounded bg-white px-2 py-1.5 text-xs text-slate-700 border border-slate-200 break-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-secondary-700 bg-white border border-secondary-300 hover:bg-secondary-50 transition-colors"
              aria-label="Copy invite link"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            <p className="mt-1.5 text-xs text-slate-500">Expires in 7 days · One-time use</p>
          </div>
        )}

        {/* Pending invitations */}
        {!loadingInvites && invitations.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Pending Invitations
            </p>
            <ul className="space-y-2">
              {invitations.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2"
                >
                  <div>
                    <p className="text-xs text-slate-600">Sent {formatRelativeTime(inv.createdAt)}</p>
                    <p className="text-xs text-slate-400">Expires {formatDate(inv.expiresAt)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => revokeMutation.mutate(inv.id)}
                    disabled={revokeMutation.isPending}
                    className="text-xs text-danger-500 hover:text-danger-700 font-medium transition-colors"
                    aria-label="Revoke invitation"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// FamilyPage
// ---------------------------------------------------------------------------
export default function FamilyPage(): React.ReactElement {
  const { appUser, refreshUser } = useAuth();
  const familyId = appUser?.familyId ?? '';
  const queryClient = useQueryClient();

  // Ensure appUser.familyId is populated — catches cases where the family was
  // created server-side after the last auth fetch (e.g. bootstrap script).
  React.useEffect(() => {
    if (!familyId) {
      void refreshUser();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showAddMember, setShowAddMember] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const { data: familyData } = useQuery<{ family: Family }>({
    queryKey: ['family', familyId],
    queryFn: () => get<{ family: Family }>(`/families/${familyId}`),
    enabled: !!familyId,
  });

  const { data: membersData, isLoading } = useQuery<{ members: FamilyMember[] }>({
    queryKey: ['members', familyId],
    queryFn: () => get<{ members: FamilyMember[] }>(`/families/${familyId}/members`),
    enabled: !!familyId,
  });

  const handleCopyCode = async () => {
    const code = familyData?.family?.familyCode;
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const members = membersData?.members ?? [];
  const children = members.filter((m) => m.role === 'child');
  const isPrimaryParent = appUser?.isPrimary ?? false;

  return (
    <>
    <Navbar />
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Family</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowInvite(true)}>
            <Mail size={15} className="mr-1.5" />
            Invite Parent
          </Button>
          <Button variant="primary" onClick={() => setShowAddMember(true)}>
            <UserPlus size={15} className="mr-1.5" />
            Add a Kid
          </Button>
        </div>
      </div>

      {/* Family code card */}
      {familyData?.family?.familyCode && (
        <Card className="mb-6">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Kids Login Code</p>
              <p className="mt-1 text-3xl font-extrabold tracking-widest text-primary-700">
                {familyData.family.familyCode}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                Kids enter this code + their first name + PIN to log in.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleCopyCode()}
              className="mt-3 flex items-center gap-1.5 self-start rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors sm:mt-0"
            >
              {codeCopied ? <Check size={13} className="text-secondary-600" /> : <Copy size={13} />}
              {codeCopied ? 'Copied!' : 'Copy code'}
            </button>
          </div>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      )}

      {/* No kids yet — empty state shown before ManageMembers renders children */}
      {!isLoading && children.length === 0 && (
        <Card padding="lg" shadow="none" className="mb-6">
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-50">
              <UserPlus size={24} className="text-secondary-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">No kids yet</p>
              <p className="mt-0.5 text-sm text-slate-500">
                Add your first child to start assigning chores and rewards.
              </p>
            </div>
            <Button variant="primary" onClick={() => setShowAddMember(true)}>
              Add a Kid
            </Button>
          </div>
        </Card>
      )}

      {/* Members list — handles parents + children with Edit/Remove actions */}
      {!isLoading && members.length > 0 && (
        <ManageMembers
          familyId={familyId}
          members={members}
          currentUserId={appUser?.memberId ?? ''}
          isPrimaryParent={isPrimaryParent}
        />
      )}

      {/* Add Family Member modal */}
      {showAddMember && (
        <AddFamilyMember
          familyId={familyId}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ['members', familyId] });
            setShowAddMember(false);
          }}
          onClose={() => setShowAddMember(false)}
        />
      )}

      {/* Invite Parent modal */}
      {showInvite && (
        <InviteModal familyId={familyId} onClose={() => setShowInvite(false)} />
      )}
    </div>
    </>
  );
}
